import React, { useEffect, useRef, useState } from "react"
import tw, { styled, css } from "twin.macro"
import Icon, { IconProps } from "./icon"
import { InputType } from "../input"

interface Props {
  inputRef?: React.MutableRefObject<null>
  inputValue: string
  name: string
  type?: InputType
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleBlur?: () => Promise<void> | void
  handleFocus?: () => Promise<void> | void
  disabled?: boolean
  placeholder?: string
  maxLength?: number
  autoComplete?: string
  readOnly?: boolean
  tabIndex?: number
  label?: string
  helpText?: string
  isFieldFocused?: boolean
}
interface StyleProps {
  searchBar?: boolean
  isInvalid?: boolean
  hasLabel?: boolean
  hasFocus?: boolean
}
const Field = styled.div(({ searchBar, isInvalid, hasFocus }: StyleProps) => [
  tw`w-full relative flex items-center bg-white border border-base-3 rounded`,
  isInvalid && tw`border-green`,
  hasFocus && tw`border-pink`,
  searchBar && tw`rounded-full border-base-6 border-2 pl-14 caret-purple`,
  searchBar && hasFocus && tw`border-base-6`,
])
const StyledInput = styled.input(({ hasLabel, searchBar, hasFocus }: StyleProps) => [
  tw`w-full h-11 px-3 py-1 text-left text-md bg-transparent text-base-6 font-medium focus:outline-none overflow-hidden whitespace-nowrap overflow-ellipsis`,
  hasLabel && tw`pl-0`,
  searchBar && tw`text-base-6 w-64 mobile-lg:(w-full)`,
  searchBar && hasFocus && tw`z-20`,
  css`
    &::placeholder {
      ${tw`text-base-5`}
    }
  `,
])
const Label = styled.label(() => [tw`pl-3 inline-block text-md text-base-4 font-sans font-normal pointer-events-none`])
const HelpText = styled("p")(({ isInvalid }: StyleProps) => [
  tw`mt-2 mb-0 text-sm font-bold text-base-3 self-start`,
  isInvalid && tw`text-red`,
])

export type InputProps = Props & IconProps & StyleProps
const Input = ({
  inputValue,
  placeholder,
  name,
  type,
  handleChange,
  handleKeyDown,
  handleKeyUp,
  handleFocus,
  handleBlur,
  maxLength,
  disabled,
  onClick,
  autoComplete,
  readOnly,
  tabIndex,
  searchBar,
  icon,
  iconPosition,
  label,
  helpText,
  isInvalid,
  isFieldFocused,
}: InputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [hasFocus, setFocus] = useState(isFieldFocused)

  useEffect(() => {
    if (isFieldFocused) {
      setFocus(true)
      inputRef && inputRef.current && inputRef.current.focus()
    }
  }, [isFieldFocused])

  return (
    <>
      <Field searchBar={searchBar} isInvalid={isInvalid} hasFocus={hasFocus}>
        {label && (
          <Label data-testid={`${name}-Label`} htmlFor={`${name}-id`}>
            {label}
          </Label>
        )}
        <Icon icon={icon} iconPosition={iconPosition} onClick={onClick} />
        <StyledInput
          searchBar={searchBar}
          readOnly={handleChange ? readOnly : true}
          ref={inputRef}
          data-testid={`${name}-input`}
          autoComplete={autoComplete ? autoComplete : "off"} // value has dashes will not work in chrome
          tabIndex={tabIndex}
          type={type ? type : "text"}
          name={name}
          id={`${name}-id`}
          onChange={handleChange}
          onFocus={async () => {
            setFocus(true)
            await handleFocus?.()
          }}
          onBlur={async () => {
            setFocus(false)
            await handleBlur?.()
          }}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          value={inputValue}
          placeholder={placeholder}
          aria-label={inputValue}
          maxLength={maxLength}
          disabled={disabled}
          autoFocus={isFieldFocused}
        />
      </Field>
      {helpText && (
        <HelpText data-testid={`${name}-help-text`} isInvalid={isInvalid}>
          {helpText}
        </HelpText>
      )}
    </>
  )
}
export default Input
